/**
 * Material Factory — Cached
 *
 * Creates Three.js materials for building components with consistent styling.
 * Materials are cached by their key properties so identical configurations
 * share a single GPU material instance (reduces state switches and enables
 * geometry merging via BufferGeometryUtils.mergeGeometries).
 */

import * as THREE from 'three'
import type { DoorType } from '@/lib/templates/types'

/**
 * Material configuration options
 */
export interface MaterialOptions {
  color?: string
  opacity?: number
  transparent?: boolean
  side?: THREE.Side
  receiveShadow?: boolean
  castShadow?: boolean
}

/**
 * Default colors for building components
 */
export const DEFAULT_COLORS = {
  floor: '#cccccc',
  wall: '#e8e8e8',
  partition: '#b0b0b0',
  border: '#FFD700',
  door: {
    bifold: '#4a5568',
    sliding: '#553c9a',
    rolling: '#2b6cb0',
    hydraulic: '#c53030',
    manual: '#6b7280',
  },
  safety: {
    interior: 'rgba(255, 200, 0, 0.2)',
    exterior: 'rgba(0, 200, 255, 0.2)',
  },
} as const

// ============================================================================
// MATERIAL CACHES — keyed by distinguishing properties
// ============================================================================

const wallMaterialCache = new Map<string, THREE.MeshStandardMaterial>()
const partitionMaterialCache = new Map<string, THREE.MeshStandardMaterial>()
const floorMaterialCache = new Map<string, THREE.MeshStandardMaterial>()
const doorMaterialCache = new Map<string, THREE.MeshBasicMaterial>()
const safetyMaterialCache = new Map<string, THREE.MeshBasicMaterial>()
const borderMaterialCache = new Map<string, THREE.LineBasicMaterial | THREE.LineDashedMaterial>()

/** Build a cache key from the distinguishing material properties */
function materialKey(color: string, opacity: number, transparent: boolean): string {
  return `${color}|${opacity}|${transparent ? 1 : 0}`
}

/**
 * Get or create a wall material (cached by color + opacity + transparency)
 */
export function createWallMaterial(options: MaterialOptions = {}): THREE.MeshStandardMaterial {
  const color = options.color || DEFAULT_COLORS.wall
  const opacity = options.opacity ?? 1
  const transparent = options.transparent ?? false
  const key = materialKey(color, opacity, transparent)

  let mat = wallMaterialCache.get(key)
  if (!mat) {
    mat = new THREE.MeshStandardMaterial({
      color,
      side: options.side ?? THREE.DoubleSide,
      transparent,
      opacity,
    })
    wallMaterialCache.set(key, mat)
  }
  return mat
}

/**
 * Get or create a partition wall material (cached)
 */
export function createPartitionMaterial(options: MaterialOptions = {}): THREE.MeshStandardMaterial {
  const color = options.color || DEFAULT_COLORS.partition
  const opacity = options.opacity ?? 1
  const transparent = options.transparent ?? false
  const key = materialKey(color, opacity, transparent)

  let mat = partitionMaterialCache.get(key)
  if (!mat) {
    mat = new THREE.MeshStandardMaterial({
      color,
      side: options.side ?? THREE.DoubleSide,
      transparent,
      opacity,
    })
    partitionMaterialCache.set(key, mat)
  }
  return mat
}

/**
 * Get or create a floor material (cached)
 */
export function createFloorMaterial(options: MaterialOptions = {}): THREE.MeshStandardMaterial {
  const color = options.color || DEFAULT_COLORS.floor
  const opacity = options.opacity ?? 1
  const transparent = options.transparent ?? false
  const key = materialKey(color, opacity, transparent)

  let mat = floorMaterialCache.get(key)
  if (!mat) {
    mat = new THREE.MeshStandardMaterial({
      color,
      side: options.side ?? THREE.DoubleSide,
      transparent,
      opacity,
    })
    floorMaterialCache.set(key, mat)
  }
  return mat
}

/**
 * Get or create a door material based on door type (cached)
 */
export function createDoorMaterial(
  type: DoorType,
  options: MaterialOptions = {}
): THREE.MeshBasicMaterial {
  const color = options.color || DEFAULT_COLORS.door[type] || DEFAULT_COLORS.door.bifold
  const opacity = options.opacity ?? 0.3
  const transparent = options.transparent ?? true
  const key = `${type}|${materialKey(color, opacity, transparent)}`

  let mat = doorMaterialCache.get(key)
  if (!mat) {
    mat = new THREE.MeshBasicMaterial({
      color,
      side: options.side ?? THREE.DoubleSide,
      transparent,
      opacity,
    })
    doorMaterialCache.set(key, mat)
  }
  return mat
}

/**
 * Get or create a safety zone material (cached)
 */
export function createSafetyZoneMaterial(
  zone: 'interior' | 'exterior',
  options: MaterialOptions = {}
): THREE.MeshBasicMaterial {
  const defaultColor = zone === 'interior'
    ? DEFAULT_COLORS.safety.interior
    : DEFAULT_COLORS.safety.exterior
  const color = options.color || defaultColor
  const opacity = options.opacity ?? 0.2
  const key = `${zone}|${materialKey(color, opacity, true)}`

  let mat = safetyMaterialCache.get(key)
  if (!mat) {
    mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity,
      side: THREE.DoubleSide,
    })
    safetyMaterialCache.set(key, mat)
  }
  return mat
}

/**
 * Apply shadow settings to a mesh
 */
export function applyShadowSettings(
  mesh: THREE.Mesh,
  receiveShadow: boolean = true,
  castShadow: boolean = true
): void {
  mesh.receiveShadow = receiveShadow
  mesh.castShadow = castShadow
}

// ============================================================================
// BORDER MATERIALS (for outdoor areas — LineBasicMaterial / LineDashedMaterial)
// ============================================================================

export interface BorderMaterialOptions {
  color?: string
  style?: 'solid' | 'dashed'
  linewidth?: number
  dashSize?: number
  gapSize?: number
}

/**
 * Get or create a border line material (cached)
 */
export function createBorderMaterial(
  options: BorderMaterialOptions = {}
): THREE.LineBasicMaterial | THREE.LineDashedMaterial {
  const color = options.color || DEFAULT_COLORS.border
  const style = options.style || 'solid'
  const key = `${style}|${color}`

  let mat = borderMaterialCache.get(key)
  if (!mat) {
    if (style === 'dashed') {
      mat = new THREE.LineDashedMaterial({
        color,
        dashSize: options.dashSize ?? 3,
        gapSize: options.gapSize ?? 2,
      })
    } else {
      mat = new THREE.LineBasicMaterial({ color })
    }
    borderMaterialCache.set(key, mat)
  }
  return mat
}
